// Core singleton, not the `aws-amplify` root export — see the note in
// `src/context/AuthContext.tsx`. `DefaultAmplify.getConfig()` only delegates
// here, so this reads the exact same configuration; importing the root export
// would drag the unused identity-pool credentials provider back into the bundle.
import { Amplify } from '@aws-amplify/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { formatFullAddress } from 'src/helper/address-display';
import { durationParts, isValidTimezone, parseLenient } from 'src/helper/date';
import { logger, normalizeError } from 'src/helper/logger';
import type {
  AddressInfo,
  DomainType,
  GenericObj,
  InfinityScrollObjType,
  MediaType,
  ViewTypes,
} from 'src/helper/shared-types';
import type { ViewListType } from 'src/redux/list-view/types';
import type { InitialStateType } from 'src/redux/service-request/types';

export type SetErrorFunction = (
  name: string,
  error: Partial<{ type: string; message: string }>,
) => void;

export const generateRandomString = () =>
  Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

export const createAbortSignal = () => {
  const controller = new AbortController();
  return controller;
};
export const formatZipCode = (input: string): string => {
  const numbers = input.replace(/\D/g, '');
  let formatted = '';
  formatted = numbers.slice(0, 5);
  return formatted;
};
export const catchHandler = (
  error: {
    response: {
      message: string;
      errors: { [key: string]: string[] };
    };
  },
  setError: SetErrorFunction,
  setFieldError: boolean,
) => {
  if (error?.response?.errors) {
    Object.keys(error?.response?.errors).forEach((field) => {
      setError(setFieldError ? [field].toString() : 'general_errors', {
        type: 'manual',
        message: error?.response?.errors[field][0],
      });
    });
  } else if (error?.response?.message) {
    setError('general_errors', {
      type: 'manual',
      message: error?.response?.message,
    });
  }
};

// Single-flight guard: while a getAccessToken() call is in flight, concurrent
// callers share the same promise instead of each triggering their own
// fetchAuthSession() (which can hit Cognito). Cleared as soon as the promise
// settles (see .finally below), so this ONLY collapses concurrent calls — it is
// NOT a TTL/token cache. The next call after settle re-fetches, avoiding any
// risk of serving an expired token.
let inFlightToken: Promise<string> | null = null;

export const getAccessToken = async (): Promise<string> => {
  if (inFlightToken) {
    return inFlightToken;
  }

  // The try/catch lives inside the wrapped async so the shared promise always
  // resolves to a string (never rejects), keeping behavior identical for callers.
  inFlightToken = (async (): Promise<string> => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (token) {
        return token;
      }
      // No token in session — expected during logout or session expiry, not worth logging
      return '';
    } catch (error) {
      // fetchAuthSession itself threw — Cognito SDK error or network failure, not routine
      logger.warn('[Auth] fetchAuthSession threw unexpectedly', {
        // pathname, not href: this fires on password-reset and vendor-onboarding
        // URLs whose query string carries `signature`/`expires`/`token`. The
        // telemetry scrubber would redact those anyway, but there is no reason
        // to hand it a credential in the first place.
        error: normalizeError(error),
        url: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      return '';
    }
  })().finally(() => {
    // Clear once settled so the next call re-fetches a fresh token.
    inFlightToken = null;
  });

  return inFlightToken;
};

export const getUserPoolId = () => {
  try {
    // Get from Amplify config
    const config = Amplify.getConfig();
    return config?.Auth?.Cognito?.userPoolId || '';
  } catch {
    return '';
  }
};

export const addOverlayInSidePanel = () => {
  const overlay = document.createElement('div');
  overlay.className = 'side-panel-overlay';
  document.querySelector('.offcanvas')?.appendChild(overlay);
  document.body.classList.add('offcanvas-overlay');
};

export const removeOverlayInSidePanel = () => {
  const overlay = document.querySelector('.side-panel-overlay');
  if (overlay) {
    overlay.remove();
  }
  document.body.classList.remove('offcanvas-overlay');
};

export const addOverlayInListingView = (id: string) => {
  const overlay = document.createElement('div');
  const listingLayout = document.getElementById('listingLayout');
  const overflow = listingLayout?.style.overflow;

  overlay.className = `listing-view-overlay listing-view-overlay-${id}`;
  document.querySelector('.listing-view')?.appendChild(overlay);

  if (listingLayout && (!overflow || overflow === 'auto')) {
    listingLayout.style.overflow = 'hidden';
  }
};

export const removeOverlayInListingView = (id: string) => {
  const overlay = document.querySelector(`.listing-view-overlay-${id}`);
  const listingLayout = document.getElementById('listingLayout');
  const overflow = listingLayout?.style.overflow;

  overlay?.remove();

  const remainingOverlays = document.querySelectorAll('.listing-view-overlay');
  if (listingLayout && remainingOverlays.length === 0 && (!overflow || overflow === 'hidden')) {
    listingLayout.style.overflow = 'auto';
  }
};
// Moved verbatim to the leaf module date-conversion.ts; re-exported here so
// every existing 'src/helper/utils' import keeps working unchanged.
export { convertUtcToLocal, convertUtcToRelevantTimezone } from './date-conversion';

/**
 * Transitional structural date shapes for the moment→AppDate migration: satisfied
 * by BOTH a moment instance (not-yet-migrated callers, e.g. BlockOutTimeModal /
 * TechnicianHours / SchedulingBlockoutModal) and an AppDate. Method parameters are
 * deliberately loose (`unknown`) so both libraries' signatures assign; the
 * implementations only call the shared subset. Collapse to AppDate in PR-6.
 */
type TimeOptionCursor = {
  isBefore(other: unknown): boolean;
  format(template?: string): string;
  add(amount: number, unit: 'minutes'): TimeOptionCursor;
};

type AppointmentTimeLike = {
  format(template?: string): string;
  minute(): number;
  isSame(other: unknown, unit: 'day'): boolean;
};

export const generateTimeOptions = (
  start: TimeOptionCursor,
  end: TimeOptionCursor,
  interval: number,
  format: string,
) => {
  const result: Array<{ value: string; label: string }> = [];

  // Reassigned-cursor walk: with an AppDate each add() returns a NEW instance (the
  // loop terminates because the cursor is reassigned); with a moment input add()
  // mutates and returns the SAME instance, so the caller's `start` still advances
  // exactly as it always has (golden-pinned via expectedStartAfterCall).
  let cursor = start;
  while (cursor.isBefore(end)) {
    result.push({ value: cursor.format(format), label: cursor.format(format) });
    cursor = cursor.add(interval, 'minutes');
  }

  return result;
};

export const passwordSchema = (input: string) => {
  if (input?.length) {
    if (input?.length >= 8 && input?.length <= 50) {
      if (!/^(?=.*\d)(?=.*[!@#$%^&*/\\(),.><':;|_~`=[\]{}"])/.test(input)) {
        return 'Must contain at least one number & special character';
      }
      if (!/^(?=.*[a-z])/.test(input)) {
        return 'Must contain at least one lowercase letter';
      }
      if (!/^(?=.*[A-Z])/.test(input)) {
        return 'Must contain at least one uppercase letter';
      }
      if (!/^\S*$/.test(input)) {
        return 'Must not contain spaces';
      }
    } else {
      return input?.length >= 50
        ? 'Must be at most 50 characters'
        : 'Must be at least 8 characters';
    }
  }
  return null;
};

export const isMappingRendered = (
  dataSourceLength: number,
  tableId: string,
  infinityScrolling?: InfinityScrollObjType,
  nextPage?: string,
  tableCollapsed?: boolean,
  isMobile?: boolean,
) => {
  const element = document.getElementById(tableId);
  let isMappingCompleted: boolean = false;
  if (isMobile) {
    const listItems = element?.getElementsByClassName(
      'list-item',
    ) as HTMLCollectionOf<HTMLTableRowElement>;
    isMappingCompleted = (listItems?.length || 0) >= dataSourceLength;
  } else {
    const tableBodyElement = element?.getElementsByTagName(
      'tbody',
    ) as HTMLCollectionOf<HTMLTableSectionElement>;

    const trElements = tableBodyElement
      ?.item(0)
      ?.getElementsByTagName('tr') as HTMLCollectionOf<HTMLTableRowElement>;

    isMappingCompleted = (trElements?.length || 0) >= dataSourceLength;
  }
  if (
    dataSourceLength > 0 &&
    ((tableCollapsed && !isMappingCompleted) || isMappingCompleted) &&
    infinityScrolling &&
    nextPage
  ) {
    return true;
  }
  return false;
};

export const getNameAbbreviation = (firstName: string, lastName: string) => {
  const firstNameAbbreviation = firstName?.charAt(0) || '';
  const lastNameAbbreviation = lastName?.charAt(0) || '';
  return `${firstNameAbbreviation}${lastNameAbbreviation}`;
};
export const getRoleBadgeColors = (role: string) => {
  const roleBadgeClassMap: { [key: string]: string } = {
    Technician: 'orange',
    Owner: 'info',
  };
  return roleBadgeClassMap[role] || 'danger';
};

export const hasMappingDataRendered = (
  dataSourceLength: number,
  containerId: string,
  nextPage?: string,
) => {
  const element = document.getElementById(containerId);

  const itemArray = element?.getElementsByClassName(
    'activity-log-item',
  ) as HTMLCollectionOf<HTMLDivElement>;

  const isMappingCompleted = (itemArray?.length || 0) >= dataSourceLength;

  if (dataSourceLength > 0 && isMappingCompleted && nextPage) {
    return true;
  }
  return false;
};

export const generateUniqueId = () => URL.createObjectURL(new Blob([])).slice(-36);

export const isObject = (value: any) =>
  typeof value === 'object' && !Array.isArray(value) && value !== null;

export const generateAppointmentString = (
  appointmentStartTime: boolean | AppointmentTimeLike,
  appointmentEndTime: boolean | AppointmentTimeLike,
) => {
  let appointment;
  if (typeof appointmentStartTime !== 'boolean' && typeof appointmentEndTime !== 'boolean') {
    const isSameAM =
      appointmentStartTime?.format('A')?.includes('AM') &&
      appointmentEndTime?.format('A')?.includes('AM');
    const isSamePM =
      appointmentStartTime?.format('A')?.includes('PM') &&
      appointmentEndTime?.format('A')?.includes('PM');
    if (appointmentStartTime.isSame(appointmentEndTime, 'day')) {
      // Same start and end date
      appointment = `${
        appointmentStartTime?.minute() > 1
          ? appointmentStartTime?.format(
              `ddd MMM DD YYYY, h:mm${isSameAM || isSamePM ? '' : ' A '}`,
            )
          : appointmentStartTime?.format(`ddd MMM DD YYYY, h${isSameAM || isSamePM ? '' : ' A '}`)
      }-${
        appointmentEndTime?.minute() > 1
          ? appointmentEndTime?.format('h:mm A zz')
          : appointmentEndTime?.format('h A zz')
      }`;
    } else {
      // Different start and end date
      appointment = `${
        appointmentStartTime?.minute() > 1
          ? appointmentStartTime?.format(
              `ddd MMM DD YYYY, h:mm${isSameAM || isSamePM ? '' : ' A '}`,
            )
          : appointmentStartTime?.format(`ddd MMM DD YYYY, h${isSameAM || isSamePM ? '' : ' A '}`)
      }-${
        appointmentEndTime?.minute() > 1
          ? appointmentEndTime?.format('ddd MMM DD YYYY, h:mm A zz')
          : appointmentEndTime?.format('ddd MMM DD YYYY, h A zz')
      }`;
    }
    return appointment;
  }
  return '-';
};

export const generateAgendaAppointmentString = (
  appointmentStartTime: boolean | AppointmentTimeLike,
  appointmentEndTime: boolean | AppointmentTimeLike,
) => {
  let appointment;
  if (typeof appointmentStartTime !== 'boolean' && typeof appointmentEndTime !== 'boolean') {
    // Same start and end date
    appointment = `${
      appointmentStartTime?.minute() > 1
        ? appointmentStartTime?.format('h:mma')
        : appointmentStartTime?.format('ha')
    }-${
      appointmentEndTime?.minute() > 1
        ? appointmentEndTime?.format('h:mma')
        : appointmentEndTime?.format('ha')
    }`;

    return appointment;
  }
  return '-';
};

export const humanize = (seconds: number = 0, trunc = 0) => {
  let str = '';
  let times = 0;
  // durationParts reproduces moment's month-bubbled decomposition (146097/400yr
  // constant); dayjs.duration's fixed 30-day months would drift on >= 1-month spans.
  const duration = durationParts(seconds);
  (['years', 'months', 'days', 'hours', 'minutes', 'seconds'] as const).every((period) => {
    const inPeriod = duration[period];
    if (inPeriod && inPeriod > 0) {
      if (times >= trunc) {
        return false;
      }
      times += 1;
      str += `${inPeriod} ${period} `;
    }

    return true;
  });
  str = str !== '' ? str : '-';
  return str;
};

// Builds a "{count} {singular|plural}" label, e.g. pluralize(2, 'override', 'overrides').
export const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export const copyToClipboard = async (text: string) => {
  try {
    await navigator?.clipboard?.writeText(text);
  } catch {
    // console.log();
  }
};

export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'flv', 'm4v'] as const;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'] as const;

/**
 * Detects HEIC/HEIF image files. Chrome cannot decode these natively, so the
 * upload pipeline must skip client-side thumbnail generation and rely on the
 * backend to produce a thumbnail.
 *
 * Both MIME and extension checks are needed: Chrome on Linux (and some
 * non-standard sources) leaves `file.type` empty for HEIC/HEIF.
 */
export const isHeicFile = (file: File): boolean => {
  const type = file.type?.toLowerCase() ?? '';
  if (type === 'image/heic' || type === 'image/heif') return true;
  const name = file.name?.toLowerCase() ?? '';
  return name.endsWith('.heic') || name.endsWith('.heif');
};

/**
 * Detects whether a file is a video based on MIME type or extension.
 * Handles cases where file.type may be empty (e.g., iOS Safari).
 */
export const isVideoFile = (file: File): boolean => {
  if (file.type?.startsWith('video/')) return true;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTENSIONS.includes(extension as (typeof VIDEO_EXTENSIONS)[number]);
};

/**
 * Detects whether a file is specifically an MP4 file based on MIME type or extension.
 * More restrictive than isVideoFile — only returns true for MP4 format.
 */
export const isMp4File = (file: File): boolean => {
  if (file.type === 'video/mp4') return true;
  return file.name.split('.').pop()?.toLowerCase() === 'mp4';
};

/**
 * Detects a QuickTime/.mov file — the other container (besides MP4) the
 * backend accepts as-is. Used alongside isMp4File to decide whether an
 * original is safe to upload without client-side conversion.
 */
export const isMovFile = (file: File): boolean => {
  if (file.type === 'video/quicktime') return true;
  return file.name.split('.').pop()?.toLowerCase() === 'mov';
};

// Containers Mediabunny can't demux at all (AVI, WMV/ASF, FLV have no reader
// registered) or whose only real-world codec (Theora, inside an Ogg/.ogv
// container) WebCodecs can't decode — so client-side conversion always throws.
// Checked independently of isVideoFile so it still catches these even when the
// browser leaves `file.type` empty.
const UNSUPPORTED_VIDEO_EXTENSIONS = ['avi', 'wmv', 'flv', 'ogv'] as const;
const UNSUPPORTED_VIDEO_MIME_TYPES = [
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/x-flv',
  'video/ogg',
] as const;

/**
 * Fast-path check for video formats known to always fail client-side
 * conversion, so they can be rejected before an upload/conversion attempt
 * ever starts (see SAAS-995 — letting these reach the upload pipeline led to
 * a failed-upload/retry loop instead of an upfront validation message).
 */
export const isUnsupportedVideoFormat = (file: File): boolean => {
  const type = file.type?.toLowerCase() ?? '';
  if (
    UNSUPPORTED_VIDEO_MIME_TYPES.includes(type as (typeof UNSUPPORTED_VIDEO_MIME_TYPES)[number])
  ) {
    return true;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return UNSUPPORTED_VIDEO_EXTENSIONS.includes(
    extension as (typeof UNSUPPORTED_VIDEO_EXTENSIONS)[number],
  );
};

/**
 * Builds the `accept` map for react-dropzone from a list of file extensions.
 * HEIC/HEIF and WebP are automatically included whenever any image type is
 * present, because Chrome on some platforms omits `file.type` for HEIC files.
 */
export const buildDropzoneAcceptTypes = (extensions: string[]): Record<string, string[]> => {
  const imageFileTypes = ['jpeg', 'png', 'gif', 'bmp', 'svg'];
  const documentFileTypes = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'txt'];

  const acceptedFiles: Record<string, string[]> = {};
  const hasImageType = extensions?.some((ext) => imageFileTypes.includes(ext)) ?? false;

  extensions?.forEach((extension) => {
    if (imageFileTypes.includes(extension)) {
      acceptedFiles[`image/${extension}`] = [];
    } else if (documentFileTypes.includes(extension)) {
      acceptedFiles[`text/${extension}`] = [`.${extension}`];
    }
  });

  if (hasImageType) {
    // Always include HEIC/HEIF so Safari doesn't auto-convert them to PNG before
    // the onDrop handler fires. The upload gate is enforced in onDrop via isHeicFile.
    acceptedFiles['image/heic'] = ['.heic'];
    acceptedFiles['image/heif'] = ['.heif'];
    acceptedFiles['image/webp'] = ['.webp'];
  }

  return { ...acceptedFiles, 'video/*': [] };
};

/**
 * Generic, human-readable category list of what buildDropzoneAcceptTypes accepts,
 * used for the "Only … files are accepted!" validation message: the broad
 * "image"/"document" categories (matching the configured types, which also cover
 * HEIC/HEIF/WEBP) plus the always-accepted "video".
 */
export const buildAcceptedFileTypesLabel = (extensions: string[]): string => {
  const imageFileTypes = ['jpeg', 'png', 'gif', 'bmp', 'svg'];
  const documentFileTypes = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'txt'];

  const categories: string[] = [];
  if (extensions?.some((ext) => imageFileTypes.includes(ext))) categories.push('image');
  if (extensions?.some((ext) => documentFileTypes.includes(ext))) categories.push('document');
  categories.push('video'); // video/* is always accepted

  if (categories.length === 1) return categories[0];
  return `${categories.slice(0, -1).join(', ')} and ${categories[categories.length - 1]}`;
};

export const getFileTypeFromMIMEType = (fileMIMEType: string) => {
  const raw = fileMIMEType?.trim()?.toLowerCase() ?? '';
  if (!raw) return undefined;

  // Full MIME type (e.g. "image/jpg", "video/mp4")
  if (raw.includes('/')) {
    const [type] = raw.split('/');
    return type;
  }

  // Extension only (e.g. "jpg", "png", "mp4")
  if (VIDEO_EXTENSIONS.includes(raw as (typeof VIDEO_EXTENSIONS)[number])) return 'video';
  if (IMAGE_EXTENSIONS.includes(raw as (typeof IMAGE_EXTENSIONS)[number])) return 'image';

  // Type only (e.g. "image", "video") or other
  return raw;
};

export const buildFileUrl = (url: string): string => {
  if (!url) return '';

  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return url;
  }

  const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '';

  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanPath = url.startsWith('/') ? url : `/${url}`;

  return `${cleanBaseUrl}${cleanPath}`;
};

export const filterJobMediaByType = (
  dataSource: Array<MediaType>,
  filterKeyValue: MediaType['media_type'],
) => dataSource?.filter((item) => item?.media_type === filterKeyValue) || [];

// Excludes isUploadFailed on purpose: a failed or cancelled item shouldn't keep a
// form's submit button disabled indefinitely — normal validation takes over instead.
export const isAnyMediaProcessing = (media?: MediaType[]): boolean =>
  !!media?.some((item) => item?.isConverting || item?.isMediaUploading);

export const scrollToLastChild = (containerId: string) => {
  const container = document.getElementById(containerId);

  if (container) {
    const lastChild = container.lastElementChild;

    if (lastChild) {
      lastChild.scrollIntoView({ behavior: 'instant', block: 'end' });
    }
  }
};

/**
 * Creates an abbreviation from the given text by taking the first character
 * (or special character) of each word and converting them to uppercase.
 *
 * @param {string} text - The input text from which to create the abbreviation.
 * @returns {string} The generated abbreviation.
 */
export const createAbbreviation = (text: string) => {
  // Check if the input text is empty or contains only whitespace
  if (!text?.trim()) return '';

  const splittedString = (text?.match(/\b\w/g) || []).join('').toUpperCase();
  return `${splittedString?.[0]}${splittedString?.[Number(splittedString?.length) - 1]}`;
};

export const generateFullAddress = (address: AddressInfo) => {
  return formatFullAddress(address);
};

export const formatNumber = (
  num: number | string,
  fractionDigits: number = 0,
  hideSymbol: boolean = false,
  extraProps?: Record<string, any>,
): string => {
  // Ensure that num is a valid number or can be converted to one
  const numericValue = typeof num === 'string' ? parseFloat(num) : num;

  const numberFormatter = new Intl.NumberFormat('en-US', {
    ...(hideSymbol ? {} : { style: 'currency' }),
    ...(extraProps || {}),
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return numberFormatter.format(numericValue);
};

export const generateScheduleDateString = (
  appointmentStartTime: boolean | AppointmentTimeLike,
  appointmentEndTime: boolean | AppointmentTimeLike,
  format: string = 'ddd MMM DD ,',
): string => {
  // Handle cases where either time is boolean false
  if (typeof appointmentStartTime !== 'boolean' && typeof appointmentEndTime !== 'boolean') {
    // Format a single time with minutes check
    const formatTime = (time: AppointmentTimeLike, includeDate: boolean = true): string => {
      const timeFormat = time.minute() > 0 ? 'h:mm A' : 'h A';
      return includeDate ? time.format(`${format} ${timeFormat}`) : time.format(timeFormat);
    };

    if (appointmentStartTime.isSame(appointmentEndTime, 'day')) {
      // Same day: include date only at start
      return `${formatTime(appointmentStartTime)}-${formatTime(appointmentEndTime, false)}`;
    }
    // Different days: include date for both
    return `${formatTime(appointmentStartTime)}-${formatTime(appointmentEndTime)}`;
  }
  return '-';
};

export const checkWOListUpdate = (workOrderId: string, type?: 'tags') => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname?.includes('work-orders')) {
    const listUpdateEvent = new CustomEvent('checkWOListUpdate', {
      detail: { woId: workOrderId, type },
    });
    document.dispatchEvent(listUpdateEvent);
  }
};

// Mirror of checkWOListUpdate for the Service Requests list. Dispatched from a
// self-edit (e.g. an assignee change) so the grouped SR view reconciles its
// group counts + item placement without a page refresh — the initiator receives
// no Pusher echo of its own change (broadcasts are toOthers). Consumed by the
// `checkSRListUpdate` listener in the SR Listing; only fires on the SR route.
export const checkSRListUpdate = (serviceRequestId: string, pusherEvent: string = '') => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname?.includes('service-requests')) {
    const listUpdateEvent = new CustomEvent('checkSRListUpdate', {
      detail: { srId: serviceRequestId, pusherEvent },
    });
    document.dispatchEvent(listUpdateEvent);
  }
};

// Mirror of checkWOListUpdate for the Quotes list. Reuses the existing
// `quoteListEntrySynced` listener so a self-edit reconciles the grouped quote
// view (counts + placement) without a refresh; only fires on the quotes route.
//
// ORI-698: the Work Order side panel can be opened in place over the /quotes route,
// where the edited entity is a *work order* but the list rows are its *quotes* (one WO
// backs N quote rows). In that case the caller has no single quote id, so it passes
// `workOrderId` and the Quotes list resolves every visible row for that work order. The
// quote-row edit and the Pusher echo keep passing `quoteId` and are unchanged. The
// listener prefers `quoteId` when present, so a call carrying both stays single-row.
export const checkQuoteListUpdate = ({
  quoteId = '',
  workOrderId = '',
  event = '',
}: {
  quoteId?: string;
  workOrderId?: string;
  event?: string;
} = {}) => {
  if (typeof window === 'undefined') return;
  if (!quoteId && !workOrderId) return;
  if (window.location.pathname?.includes('quotes')) {
    const listUpdateEvent = new CustomEvent('quoteListEntrySynced', {
      detail: { quoteId, workOrderId, event },
    });
    document.dispatchEvent(listUpdateEvent);
  }
};

export const openLinkInNewTab = (url: string) => {
  if (typeof window === 'undefined') return;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const getQuoteStatusLabel = (value: string, label: string) =>
  value === 'partially-approved' ? 'Approved' : label;

export const checkDomainType = (url: string): DomainType => {
  if (url?.split('.')?.slice(-2)?.join('.')?.includes('vendor')) {
    return 'vendor';
  }
  return 'coordinator';
};
export const getSubdomain = () => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname?.split('.')[0] || '';
};
export enum IssueAbilities {
  EDIT = 'edit',
  ASSIGN = 'assign',
  DELETE = 'delete',
  CANCEL = 'cancel',
  ADD_NEW_WORK_ORDER = 'add_new_work_order',
  RESTORE = 'restore',
  UNASSIGN = 'unassign',
  ADD_QUOTE_TASK = 'add_quote_task',
}

export const sortData = (
  data: Record<string, any>,
  sortKey: string,
  sortOrder: 'asc' | 'desc' = 'asc',
) => {
  const sortedEntries = Object.entries(data).sort(([, a], [, b]) => {
    const valueFromA = a[sortKey];
    const valueFromB = b[sortKey];

    if (valueFromA === valueFromB) {
      return 0;
    }

    const isAscendingOrder = sortOrder === 'asc';
    const comparison = isAscendingOrder ? valueFromA > valueFromB : valueFromA < valueFromB;

    return comparison ? 1 : -1;
  });

  return Object.fromEntries(sortedEntries);
};

export const getValueByPath = (obj: GenericObj, path: string) =>
  path.split('.').reduce((acc, part) => acc?.[part], obj);
/**
 * Updates the URL for opening the Service Request (SR) side panel.
 *
 * @param {string} id - The ID of the Service Request to open.
 * @param {string} [panel] - Optional panel to open.
 * @returns {void}
 * */
export const updateUrlForOpenSrSidePanel = (
  srId: string,
  panel?: string,
  options?: Partial<InitialStateType['showSidePanel']>,
): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('woId');
  url.searchParams.set('srId', srId);
  window.history.pushState({ panel, options }, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const updateUrlForCloseSrSidePanel = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('srId');
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * Updates the URL for opening the Work Order (WO) side panel.
 *
 * @param {string} id - The ID of the Work Order to open.
 * @param {string} [panel] - Optional panel to open.
 * @returns {void}
 * */
export const updateUrlForOpenWoSidePanel = (woId: string, panel?: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('srId');
  url.searchParams.delete('reqId');
  url.searchParams.set('woId', woId);
  window.history.pushState({ panel }, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * Opens a Work Order side panel with the messages panel auto-opened.
 * Uses URL parameter to signal that the messages panel should be opened.
 *
 * @param {string} woId - The ID of the Work Order to open.
 * @returns {void}
 */
export const updateUrlForOpenWoSidePanelWithMessages = (woId: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('srId');
  url.searchParams.delete('reqId');
  url.searchParams.set('woId', woId);
  url.searchParams.set('openMessages', 'true');
  window.history.pushState({ panel: 'messages' }, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const updateUrlForCloseWoSidePanel = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('woId');
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const updateUrlForOpenVendorPortalRequestSidePanel = (id: string, panel?: string) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('reqId', id);
  window.history.pushState({ panel }, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const updateUrlForCloseVendorPortalRequestSidePanel = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('reqId');
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * Formats a raw phone number string for display.
 *  • 10 digits           → "(555) 123-4567"
 *  • 11 digits, starts 1 → strips the US country code, then formats as above
 *  • Anything else       → returned as-is (graceful pass-through for non-US numbers)
 */
export const formatPhoneDisplay = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

/**
 * Reduces a stored phone number to the 10 national digits the `(###) ###-####` masks expect.
 * Partner-synced residents arrive in whatever shape the partner sent: E.164 (`+19895550123`)
 * or already formatted (`(989) 555-0123`). Feeding those to the mask garbles them.
 *  • 11 digits, starts 1 → strips the US country code
 *  • Anything else       → the digits only
 */
export const toNationalPhoneDigits = (raw: string | null | undefined): string => {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
};

export const formatPhoneNumber = (input: string): string => {
  const numbers = toNationalPhoneDigits(input);
  let formatted = '';
  if (numbers.length > 0) {
    formatted = `(${numbers.substring(0, 3)})`;
  }
  if (numbers.length > 3) {
    formatted += ` ${numbers.substring(3, 6)}`;
  }
  if (numbers.length > 6) {
    formatted += `-${numbers.substring(6, 10)}`;
  }

  return formatted;
};

/**
 * Renders a UTC instant in an explicit zone.
 *
 * The zone is REQUIRED. It used to default to `'America/Chicago'`, but a default
 * parameter only fires on `undefined` and the backend emits an EMPTY STRING for a work
 * order with no zone (`ListResource.php` / `WorkOrderResource.php`:
 * `'timezone' => $this->timezone->name ?? ''`). `''` sailed past the default, failed
 * `isValidTimezone`, and fell through to the unzoned instant — i.e. the VIEWER's browser
 * zone, silently attributed to the record. Per COM-655 a record-anchored timestamp
 * displays in the record's own zone; with no zone there is nothing to display, so
 * callers must handle the absent case rather than have one invented for them here.
 *
 * The `isValidTimezone` guard below is a different concern and stays: it is moment-parity
 * for a BAD zone (moment's `.tz(unknown)` was a no-op), not a substitute for a MISSING one.
 */
export function formatDateLabel(datetime: string, timezone: string, format?: string): string {
  if (!datetime) return '';
  const fmt = format ?? 'MMM DD, h A';

  const parsed = parseLenient(datetime);
  // moment rendered an unparseable input as the lowercase 'Invalid date'; keep it verbatim.
  if (!parsed) return 'Invalid date';
  // moment's .tz(unknown) was a logged no-op that left the instant unchanged;
  // dayjs's would throw, so the zone is validated first (same fallback).
  const zoned = isValidTimezone(timezone) ? parsed.tz(timezone) : parsed;
  return zoned.format(fmt);
}

/**
 * Navigate to the default or first available view of a module.
 *
 * @param navigate - React Router's navigate function
 * @param view - Module name / view type (e.g. 'service-requests')
 * @param list - Array of views for the module
 */
export const getDefaultViewRoute = (view: ViewTypes, list?: ViewListType[]): string => {
  const defaultView = list?.find((v) => v?.is_default);
  const targetViewId = defaultView ? defaultView?.view?.value : list?.[0]?.view?.value;

  if (!targetViewId) {
    return '*';
  }

  return `/${view}/v/l/${targetViewId}`;
};

/**
 * Converts cents to dollars
 * @param cents - The amount in cents
 * @returns The amount in dollars
 */
export const convertCentsToDollar = (cents: number): number => cents / 100;

/**
 * Converts dollars to cents
 * @param dollars - The amount in dollars
 * @returns The amount in cents
 */
export const convertDollarsToCents = (dollars: number): number => Math.round(dollars * 100);

/**
 * Determines if the notifications button should be shown based on user permissions.
 * Hide if user has all three: Organization.Settings.view,
 * Organization.Template.update, RoleManagement.Role.manage
 * This is to avoid showing the notifications button to vendors who have all three permissions.
 * Temporaly solution until we have a proper permission system on the BE.
 */
export const shouldShowNotificationsButton = (permissions: string[] | undefined): boolean => {
  if (!permissions || permissions.length === 0) {
    return true;
  }

  const requiredPermissions = [
    'Organization.Settings.view',
    'Organization.Template.update',
    'RoleManagement.Role.manage',
  ];

  const hasAllOrgAdminPermissions = requiredPermissions.every((requiredPerm) =>
    permissions.includes(requiredPerm),
  );

  return !hasAllOrgAdminPermissions;
};

export const filterIssueMedia = (media?: MediaType[]): MediaType[] | undefined => {
  if (!media) return media;
  return media.filter((item) => item?.media_type === 'issue-media');
};

/**
 * Extracts the first human-readable message from an API error payload.
 *
 * Precedence — `error.message` wins so the pre-existing friendly toast text
 * (call sites historically read `err?.message || fallback`) is preserved; the
 * structured `error.error` shapes are used only as a fallback:
 *  - Top-level message → error.message             preferred
 *  - Plain string      → error.error "msg"         returns it as-is
 *  - Array of strings  → error.error ["msg1", …]   returns "msg1"
 *  - Object of arrays  → error.error { key: ["msg"] } returns "msg"
 *
 * @param error - The rejected API payload (from rejectWithValue or a caught error)
 * @param fallback - Message to return when nothing else is found
 */
export const parseApiError = (
  error: unknown,
  fallback: string = 'Something went wrong',
): string => {
  const errorObj = error as { message?: unknown; error?: unknown } | null | undefined;
  if (typeof errorObj?.message === 'string' && errorObj.message) return errorObj.message;

  const errorField = errorObj?.error;

  if (errorField) {
    if (typeof errorField === 'string') return errorField;

    if (Array.isArray(errorField)) return errorField[0] ?? fallback;

    if (typeof errorField === 'object') {
      const firstValue = Object.values(errorField as Record<string, unknown>)[0];
      if (Array.isArray(firstValue)) return (firstValue as string[])[0] ?? fallback;
      if (typeof firstValue === 'string') return firstValue;
    }
  }

  return fallback;
};

/**
 * Reads the first available human-readable message from a caught/rejected error,
 * checking the shapes our thunk + axios layer produce, in order:
 *  - `error.payload.message`        (rejectWithValue payload)
 *  - `error.response.data.message`  (raw axios error)
 *  - `error.message`                (native Error / generic)
 *  - `fallback`                     when none are present
 *
 * Centralizes the payload/response/message ladder that was previously
 * copy-pasted into every vendor-portal side-panel catch block.
 *
 * @param error - The caught error (arrives as `unknown`)
 * @param fallback - Message to return when nothing else is found
 */
export const extractErrorMessage = (error: unknown, fallback: string): string => {
  const errorDetails = error as {
    payload?: { message?: string };
    response?: { data?: { message?: string } };
    message?: string;
  } | null;

  return (
    errorDetails?.payload?.message ||
    errorDetails?.response?.data?.message ||
    errorDetails?.message ||
    fallback
  );
};
